import AnalyticsWebClient, {
	envType,
} from '@atlassiansox/analytics-web-client';

import type { TraceContext, TraceContextState } from './types';
import { makeTraceHttpHeaders } from './utils';

export type { TraceContext } from './types';

function getEnvironment(): string {
	// Gaurd for running in the browser - if SSR (somehow) just return prod
	if (typeof window === 'undefined') {
		return envType.PROD;
	}
	const { hostname } = window.location;
	if (hostname === 'localhost' || hostname === '127.0.0.1') {
		return envType.LOCAL;
	}
	if (hostname.includes('.dev.')) {
		return envType.STAGING;
	}
	if (hostname.includes('.stg.')) {
		return envType.STAGING;
	}
	return envType.PROD;
}

// Get an AWC so that we can record issues when setting or getting the global trace context
// Heavily borrowed from browser-storage-controls package
const getAnalyticsClient: () => InstanceType<typeof AnalyticsWebClient> = (() => {
	let instance: InstanceType<typeof AnalyticsWebClient> | undefined;
	return (): InstanceType<typeof AnalyticsWebClient> => {
		if (!instance) {
			const env = getEnvironment();

			instance = new AnalyticsWebClient(
				{ env, product: 'observability' },
			);
		}
		return instance;
	};
})();

/**
 * Global state for storing the active trace context.
 * This is a simple in-memory store that maintains the current trace context
 */
const state: TraceContextState = {
	context: null,
};

/**
 * Generates a random 16-character hexadecimal span ID.
 * This is used for creating new span IDs in distributed tracing.
 *
 * @returns A 16-character hexadecimal string
 *
 * @example
 * ```typescript
 * const spanId = generateSpanId(); // e.g., "84a67a1b3ceccf27"
 * ```
 */
export function generateSpanId(): string {
	return Array.from(new Array(16), () => Math.floor(Math.random() * 16).toString(16)).join('');
}

/**
 * Generates a random 32-character hexadecimal trace ID.
 * This is used for creating new trace IDs in distributed tracing.
 *
 * @returns A 32-character hexadecimal string
 *
 * @example
 * ```typescript
 * const traceId = generateTraceId(); // e.g., "9ad3407c1a7d484b86847e7226e566d3"
 * ```
 */
export function generateTraceId(): string {
	return Array.from(new Array(32), () => Math.floor(Math.random() * 16).toString(16)).join('');
}

/**
 * Sets the active trace context.
 * This function should be called when starting a new trace to establish
 * the trace context that will be propagated to downstream operations.
 *
 * @param traceId - The trace ID to set
 * @param spanId - The span ID to set
 * @param source - The calling package/experience/what-have-you. EG 'jira.fe.backlog.create-issue_bm3'
 *
 * @example
 * ```typescript
 * setActiveTrace('9ad3407c1a7d484b86847e7226e566d3', '84a67a1b3ceccf27', 'bm3_jira.fe.inline-result.backlog.issue-create');
 * ```
 */
export function setActiveTrace(traceId: string, spanId: string, source: string): void {
	// If trace context has already been set, there has been an experience collision
	// Don't update the trace context.
	if (state.context !== null) {
		getAnalyticsClient().sendOperationalEvent({
			source,
			action: 'setActiveTrace',
			actionSubject: 'traceContext',
			attributes: {
				currentContext: state.context,
				incomingContext: { traceId, spanId },
				status: 'collision',
			},
			tags: ['observability'],
		});
	} else {
		state.context = {
			traceId,
			spanId,
		};
	}
}

/**
 * Gets the currently active trace context.
 * Returns undefined if no trace context is currently active.
 *
 * @returns The active trace context, or null if none is set
 *
 * @example
 * ```typescript
 * const trace = getActiveTrace();
 * if (trace) {
 *   console.log(`Trace ID: ${trace.traceId}, Span ID: ${trace.spanId}`);
 * }
 * ```
 */
export function getActiveTrace(): TraceContext | null {
	return state.context;
}

/**
 * Clears the active trace context.
 * This should be called when a trace is complete to ensure the context
 * doesn't leak into subsequent operations.
 *
 * @example
 * ```typescript
 * clearActiveTrace();
 * ```
 */
export function clearActiveTrace(): void {
	state.context = null;
}

/**
 * Gets the active trace context as B3 HTTP request headers.
 * Returns null if no trace context is currently active.
 *
 * @returns An object with HTTP headers to add to outgoing requests in order to propagate the trace context,
 * or null if no active trace.
 *
 * @example
 * ```typescript
 * const traceHeaders = getActiveTraceHttpHeaders();
 * if (traceHeaders) {
 *   fetch('/api/endpoint', {
 *     headers: {
 *       ...existingHeaders,
 *       ...traceHeaders,
 *     }
 *   });
 * }
 * ```
 */
export function getActiveTraceHttpHeaders(): Record<string, string> | null {
	const trace = getActiveTrace();
	if (!trace) {
		return null;
	}

	const { traceId, spanId } = trace;
	return makeTraceHttpHeaders(traceId, spanId);
}

/**
 * Gets the active trace context as URL query parameters.
 * Returns null if no trace context is currently active.
 *
 * This is useful for propagating trace context via URL query strings
 * when HTTP headers are not an option.
 *
 * @returns A URL-encoded query string with trace context, or null if no active trace
 *
 * @example
 * ```typescript
 * const queryParams = getActiveTraceAsQueryParams();
 * if (queryParams) {
 *   const url = `/api/endpoint?${queryParams}`;
 * }
 * ```
 */
export function getActiveTraceAsQueryParams(): string | null {
	const headers = getActiveTraceHttpHeaders();
	return headers ? new URLSearchParams(headers).toString().toLowerCase() : null;
}
